import { Button } from "@mui/material"
import { useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { useSearchParam, useSearchParamsBatch } from "lib/@hooks/use-search-param"
import { setReference } from "lib/@hooks/use-reference"
import { debounce } from "lib/debounce"
import Iconify from "minimals-template/components/Iconify"
import { isEnabled } from "lib/@components/feature"
import { getSharingLink } from "routes/sharing/controller/get-sharing-link"
import {
    SHARE_LINK_COMMENT_TARGET_REFERENCE,
    SHARE_LINK_COMMENT_SHARE_PARAM,
    SHARE_LINK_COMMENTS_TAB,
    buildIntroCommentTarget,
    buildScheduleCommentAnchor,
    buildTaskCommentTarget,
    shouldShowShareLinkCommentsTab,
} from "routes/schedule/plugins/share-link-comments/share-link-comment-presenters"
import { FEATURE_FLAG_SHARE_LINK_COMMENTS } from "./share-link-comments-feature-flag"
import {
    findTaskById,
    resolveSelectionAnchor,
} from "routes/schedule/plugins/share-link-comments/share-link-selection-anchor"
import { expandRangeToWordBoundaries } from "routes/schedule/plugins/share-link-comments/expand-range-to-word-boundaries"
import { getTextContextForRange } from "routes/schedule/plugins/share-link-comments/comment-text-highlight"

const SELECTION_DEBOUNCE_MS = 120
const BUTTON_OFFSET_PX = 8
const SELECTION_CONTEXT_LENGTH = 80
const TASK_ROW_PREFIX = "task-row-"
const INTRO_MARKERS = ["schedule-intro-content", "schedule-intro-note"]

// Medium-style floating "Comment" button. When a recipient highlights text inside the schedule
// content, this renders a small fixed-position button near the selection that anchors a share-link
// comment to the task/intro the selection STARTS in. Additive — it sits alongside the existing
// per-task / per-intro / folder comment buttons.
export function ShareLinkSelectionCommentButton() {
    const context = useBoundContext()
    const { isShare, isPDF, schedule, schedulePath } = context
    const setSearchParams = useSearchParamsBatch()
    const [shareLinkId] = useSearchParam("share", null)
    const [commentShareLinkId] = useSearchParam(SHARE_LINK_COMMENT_SHARE_PARAM, null)
    const [rightBarTab] = useSearchParam("ScheduleRightBarTab", null)
    const commentsLinkId = shareLinkId ?? commentShareLinkId
    const shareLink = getSharingLink.useResults(commentsLinkId)
    const containerRef = useRef(null)
    const [selectionState, setSelectionState] = useState(null)
    const isMouseSelectingRef = useRef(false)
    // The selection listener is registered once per `canShow` change but fires over the schedule's
    // whole lifetime; keep a live ref to the current schedule/path so it never reads a stale closure.
    const scheduleRef = useRef({ schedule, schedulePath })
    scheduleRef.current = { schedule, schedulePath }

    // Gate on the LINK's allowComments AND the share-link-comments feature demand (same as the other
    // comment entry points). Recipients inherit that demand from the link owner's client auth bundle
    // (ConfigureShareLinkDemands); when the feature/auth bundle is disabled the demand is absent and
    // this entry point closes rather than bleeding through the link's persisted allowComments (FIQ-1668).
    const canShowForLink =
        shouldShowShareLinkCommentsTab({
            isShare: !!isShare || !!shareLinkId,
            shareLinkId: commentsLinkId,
            shareLink,
        }) && isEnabled(FEATURE_FLAG_SHARE_LINK_COMMENTS)
    const canShow = canShowForLink && canShowInCurrentView() && !isPDF

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(trackSelection, [canShow])

    if (!canShow) return <span ref={containerRef} data-cy="share-link-selection-anchor" style={{ display: "none" }} />
    if (!selectionState) {
        return <span ref={containerRef} data-cy="share-link-selection-anchor" style={{ display: "none" }} />
    }

    return (
        <>
            <span ref={containerRef} data-cy="share-link-selection-anchor" style={{ display: "none" }} />
            {createPortal(
                <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    data-cy="share-link-selection-comment"
                    startIcon={<Iconify icon="mdi:comment-plus-outline" width={18} height={18} />}
                    onMouseDown={preventSelectionLoss}
                    onClick={commentOnSelection}
                    sx={{
                        position: "fixed",
                        top: selectionState.top,
                        left: selectionState.left,
                        transform: "translate(-50%, -100%)",
                        zIndex: (theme) => theme.zIndex.tooltip + 1,
                        boxShadow: 3,
                        whiteSpace: "nowrap",
                    }}
                >
                    Comment
                </Button>,
                document.body
            )}
        </>
    )

    function canShowInCurrentView() {
        if (isShare || shareLinkId) return true
        return rightBarTab === SHARE_LINK_COMMENTS_TAB
    }

    function trackSelection() {
        if (!canShow) return undefined
        const handle = debounce(evaluateSelection, SELECTION_DEBOUNCE_MS)
        let frame = null
        document.addEventListener("mousedown", startMouseSelection)
        document.addEventListener("selectionchange", handle)
        document.addEventListener("mouseup", finishMouseSelection)
        // Re-evaluate position (rAF-throttled, not re-debounced) on scroll/resize so the button tracks
        // the highlighted text as the content scrolls instead of staying pinned to a fixed viewport
        // spot. Scroll is CAPTURED because the schedule body scrolls in an inner container, not window.
        document.addEventListener("scroll", reposition, true)
        window.addEventListener("resize", reposition)
        return cleanup

        function reposition() {
            if (frame) return
            frame = requestAnimationFrame(() => {
                frame = null
                evaluateSelection()
            })
        }

        function cleanup() {
            handle.cancel?.()
            if (frame) cancelAnimationFrame(frame)
            document.removeEventListener("mousedown", startMouseSelection)
            document.removeEventListener("selectionchange", handle)
            document.removeEventListener("mouseup", finishMouseSelection)
            document.removeEventListener("scroll", reposition, true)
            window.removeEventListener("resize", reposition)
        }

        function startMouseSelection() {
            isMouseSelectingRef.current = true
            handle.cancel?.()
        }

        function finishMouseSelection() {
            isMouseSelectingRef.current = false
            handle.cancel?.()
            evaluateSelection()
        }
    }

    function evaluateSelection() {
        if (isMouseSelectingRef.current) return
        const next = computeSelectionState()
        setSelectionState(next)
    }

    function computeSelectionState() {
        const selection = window.getSelection?.()
        if (!selection || selection.isCollapsed || selection.rangeCount === 0) return null
        snapSelectionToWordBoundaries(selection)
        const text = selection.toString().trim()
        if (!text) return null
        const container = getScheduleContainer()
        const range = getSelectionRange(selection)
        const anchor = resolveSelectionAnchor({
            node: range?.startContainer ?? selection.anchorNode,
            endNode: range?.endContainer ?? selection.focusNode,
            container,
            allowSchedule: !!scheduleRef.current.schedule?.id,
        })
        if (!anchor) return null
        const selectionContext = getSelectionContext(anchor, selection, container)
        const rect = getSelectionRect(selection)
        if (!rect) return null
        // Hide once the selection has scrolled out of the schedule container's visible area, so the
        // button doesn't stick at the viewport edge detached from its text.
        if (isRectOutsideContainer(rect, container)) return null
        return {
            anchor,
            selectedText: text,
            selectionContext,
            top: Math.max(rect.top - BUTTON_OFFSET_PX, BUTTON_OFFSET_PX),
            left: rect.left + rect.width / 2,
        }
    }

    // Mid-word selections snap outward to whole words so anchors never store clipped fragments
    // ("companying risk assessment should be rea"). The live selection is updated so the user sees
    // the snap and the button positions against the real extent. Expansion is idempotent, so the
    // selectionchange this triggers settles on the next pass.
    function snapSelectionToWordBoundaries(selection) {
        try {
            const range = selection.getRangeAt(0)
            if (!expandRangeToWordBoundaries(range)) return
            selection.removeAllRanges()
            selection.addRange(range)
        } catch {
            // Selection APIs can throw on detached/odd ranges — keep the user's raw selection.
        }
    }

    function getScheduleContainer() {
        const marker = containerRef.current
        if (!marker) return null
        // The schedule body is the `.schedule-details` Box rendered in schedule-details.js. The slot
        // this button plugs into sits inside that body, so the nearest ancestor with that class is
        // the content boundary we constrain selections to.
        return marker.closest(".schedule-details") ?? marker.closest(".styled-bullets") ?? null
    }

    function commentOnSelection(event) {
        event.stopPropagation()
        const commentTarget = buildTargetFromAnchor()
        if (commentTarget) {
            setReference(
                {
                    ScheduleRightBarTab: SHARE_LINK_COMMENTS_TAB,
                    [SHARE_LINK_COMMENT_TARGET_REFERENCE]: commentTarget,
                },
                "tab-ScheduleRightBar-"
            )
            setSearchParams({ ScheduleRightBarTab: SHARE_LINK_COMMENTS_TAB })
        }
        clearSelection()
    }

    function buildTargetFromAnchor() {
        if (!selectionState) return null
        const { schedule: currentSchedule, schedulePath: currentSchedulePath } = scheduleRef.current
        const { anchor, selectedText, selectionContext } = selectionState
        let commentTarget
        if (anchor.type === "task") {
            const task = findTaskById(currentSchedule, anchor.taskId)
            if (!task) return null
            const targetArgs = { task, selectedText, part: anchor.part }
            if (selectionContext) targetArgs.selectionContext = selectionContext
            commentTarget = buildTaskCommentTarget(targetArgs)
        } else if (anchor.type === "intro") {
            const targetArgs = { schedule: currentSchedule, selectedText }
            if (selectionContext) targetArgs.selectionContext = selectionContext
            commentTarget = buildIntroCommentTarget(targetArgs)
        } else if (anchor.type === "schedule") {
            commentTarget = buildScheduleCommentAnchor({ schedule: currentSchedule })
        }
        if (!commentTarget) return null
        return { ...commentTarget, schedulePath: currentSchedulePath }
    }

    function clearSelection() {
        window.getSelection?.()?.removeAllRanges?.()
        setSelectionState(null)
    }

    function preventSelectionLoss(event) {
        // Keep the active selection alive through the click so the anchor/text stay resolvable.
        event.preventDefault()
    }
}

function getSelectionContext(anchor, selection, scheduleContainer) {
    const range = getSelectionRange(selection)
    if (!range) return null
    const root = getSelectionContextRoot(anchor, range, scheduleContainer)
    if (!root || !isRangeInsideRoot(range, root)) return null
    try {
        return getTextContextForRange(root, range, SELECTION_CONTEXT_LENGTH)
    } catch {
        return null
    }
}

function getSelectionContextRoot(anchor, range, scheduleContainer) {
    const startNode = range.startContainer
    if (anchor.type === "intro") return closestMatching(startNode, isIntroElement)
    if (anchor.type === "task") return closestMatching(startNode, isTaskRowElement)
    return scheduleContainer
}

function getSelectionRange(selection) {
    try {
        if (!selection || selection.rangeCount === 0) return null
        return selection.getRangeAt(0)
    } catch {
        return null
    }
}

function isRangeInsideRoot(range, root) {
    return root.contains(range.startContainer) && root.contains(range.endContainer)
}

function closestMatching(node, predicate) {
    let element = toElement(node)
    while (element) {
        if (predicate(element)) return element
        element = element.parentElement
    }
    return null
}

function isIntroElement(element) {
    const dataCy = getDataCy(element)
    return !!dataCy && INTRO_MARKERS.includes(dataCy)
}

function isTaskRowElement(element) {
    const dataCy = getDataCy(element)
    return !!dataCy && dataCy.startsWith(TASK_ROW_PREFIX)
}

function getDataCy(element) {
    if (!element || typeof element.getAttribute !== "function") return null
    return element.getAttribute("data-cy")
}

function toElement(node) {
    if (!node) return null
    if (node.nodeType === 1) return node
    return node.parentElement ?? null
}

function getSelectionRect(selection) {
    const range = selection.getRangeAt(0)
    const rect = range.getBoundingClientRect?.()
    if (!rect || (rect.width === 0 && rect.height === 0)) return null
    return rect
}

function isRectOutsideContainer(rect, container) {
    const bounds = container?.getBoundingClientRect?.()
    // No layout info (e.g. jsdom returns a zero-size rect) — don't hide on that basis.
    if (!bounds || bounds.height === 0) return false
    return rect.bottom < bounds.top || rect.top > bounds.bottom
}
